import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, type DropdownProps } from 'semantic-ui-react';
import { useGetCultureHubPillarsQuery, useGetActionPlansQuery } from '@/areas/culture-hub/api/cultureHubApi';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useInterventionsPlanSync } from '@/areas/culture-hub/hooks/useInterventionsPlanSync';
import { useActionPlanOperations } from '@/areas/culture-hub/hooks/useActionPlanOperations';
import { ACTION_PLAN_TYPE_INTERVENTIONS } from '@/areas/culture-hub/constants';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/areas/assessment-schedule/api';
import { getTeamSpecificThemeResults } from '@/areas/culture-hub/utils/themeHelpers';
import {
  getAssessmentPeriods,
  getCurrentOrganizationId,
  getLeadershipTeamsForSelectedAssessmentPeriod,
  getMemberId,
  getSelectedAssessmentPeriodId,
} from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { TopBarContext } from '@/common/layouts/AuthLayout/TopBarContext';
import MobileAssessmentPeriodList from '@/mobile/areas/dashboard/MobileAssessmentPeriodList';
import MobileTopBarSelectors from '@/mobile/components/MobileTopBarSelectors';
import {
  getFilteredPillars,
  setPillars,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import MobilePillarCard from './pillars/MobilePillarCard';
import { useNavigate } from 'react-router-dom';
import { MobilePageContent, MobilePageSection, Title, SectionHeader, SectionDescription, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';

import { AggregateType } from '@/common/constants/AggregateType';
const MobileInterventionsTab: React.FC = () => {
  const dispatch = useDispatch();
  const { assessmentPeriodId, teamId, organizationId, shouldFetchData } = useCultureHubQueryParams();
  const currentUserId = useSelector(getMemberId) ?? null;
  const filteredPillars = useSelector(getFilteredPillars);
  const assessmentPeriods = useSelector(getAssessmentPeriods);
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const leadershipTeams = useSelector(getLeadershipTeamsForSelectedAssessmentPeriod);
  const { setTopBarContent } = useContext(TopBarContext);

  const { data: pillarsData, isLoading: pillarsLoading, isFetching: pillarsFetching } = useGetCultureHubPillarsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !assessmentPeriodId || !shouldFetchData || !teamId }
  );

  const { data: actionPlansData } = useGetActionPlansQuery(
    { organizationId: organizationId!, assessmentPeriodId: assessmentPeriodId!, teamId },
    {
      skip: !assessmentPeriodId || !shouldFetchData || !organizationId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const { data: aiThemesData, isLoading: aiThemesLoading, isFetching: aiThemesFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.THEMES,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId || !organizationId }
  );

  const { data: aiSentimentData, isLoading: aiSentimentLoading, isFetching: aiSentimentFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.SENTIMENT_SCORES,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId || !organizationId }
  );

  const actionPlans = actionPlansData ?? [];
  const existingActionPlans = actionPlans;

  const handleTeamChange = React.useCallback(
    (value: string | number | undefined) => {
      const nextTeamId = typeof value === 'string' ? Number(value) : value;
      if (nextTeamId && nextTeamId !== selectedTeamId) {
        dispatch(userContextSlice.actions.setSelectedTeamId(nextTeamId));
      }
    },
    [dispatch, selectedTeamId]
  );

  const handleAssessmentPeriodChange = React.useCallback(
    (value: string | number | undefined, period?: any) => {
      const selected = period ?? assessmentPeriods?.find((p: any) => p.id === value);
      if (selected && selectedAssessmentPeriodId !== selected.id) {
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: selected,
            assessmentPeriods,
          })
        );
      }
    },
    [assessmentPeriods, dispatch, selectedAssessmentPeriodId]
  );

  React.useEffect(() => {
    if ((selectedTeamId === undefined || selectedTeamId === null) && teamId != null) {
      dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
    }
  }, [dispatch, selectedTeamId, teamId]);

  React.useEffect(() => {
    setTopBarContent(
      <MobileTopBarSelectors
        teamDropdown={
          leadershipTeams?.length ? (
            <Dropdown
              className="mobile-control-dropdown"
              placeholder="Select a team"
              fluid
              selection
              options={leadershipTeams.map(team => ({
                key: team.id,
                value: team.id,
                text: team.name,
              }))}
              value={teamId ?? undefined}
              onChange={(_event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                const nextTeamId = data.value as string | number | undefined;
                handleTeamChange(nextTeamId);
              }}
              style={{
                width: '100%',
                border: 'none',
                boxShadow: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '18px',
                fontWeight: 550,
                minHeight: '40px',
                lineHeight: '20px',
                paddingLeft: '10px',
                paddingTop: '6px',
                paddingBottom: '6px',
              }}
            />
          ) : undefined
        }
        periodDropdown={
          <MobileAssessmentPeriodList
            className="mobile-control-dropdown"
            currentOrganizationId={currentOrganizationId}
            selectedAssessmentPeriodId={selectedAssessmentPeriodId}
            onChange={handleAssessmentPeriodChange}
          />
        }
      />
    );

    return () => {
      setTopBarContent(null);
    };
  }, [
    leadershipTeams,
    teamId,
    handleTeamChange,
    currentOrganizationId,
    selectedAssessmentPeriodId,
    handleAssessmentPeriodChange,
    setTopBarContent,
  ]);

  useEffect(() => {
    if (pillarsData?.pillars) {
      const aiResults = getTeamSpecificThemeResults(aiThemesData, teamId);

      const sentimentRaw: any = aiSentimentData;
      let sentimentResults: any[] = [];

      if (Array.isArray(sentimentRaw) && sentimentRaw.length > 0 && Array.isArray(sentimentRaw[0]?.result?.team_pillar_sentiments)) {
        sentimentResults = sentimentRaw[0].result.team_pillar_sentiments as any[];
      }

      const nextPillars: Record<string, any> = { ...pillarsData.pillars };
      const normalize = (s: string) => (s || '').toString().trim();

      Object.keys(nextPillars).forEach((k) => {
        const pillar = nextPillars[k];
        const match = aiResults.find((r: any) => normalize(r.pillar_name) === normalize(pillar.name) || normalize(r.pillar_name) === normalize(pillar.key));
        if (match && Array.isArray(match.themes)) {
          const themes = match.themes.slice(0, 5).map((t: any) => ({
            key: (t.theme_title || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            label: t.theme_title,
            count: t.feedback_count,
            description: t.theme_description,
            representative_quotes: Array.isArray(t.representative_quotes) ? t.representative_quotes : [],
            sentiment: t.strength,
            signal_strength: typeof t.signal_strength === 'number' ? t.signal_strength : undefined,
          }));
          nextPillars[k] = { ...pillar, themes };
        }

        const sentimentMatch = sentimentResults.find((r: any) => {
          if (!teamId || r.team_id == null) return false;
          if (Number(r.team_id) !== Number(teamId)) return false;
          return normalize(r.pillar_name) === normalize(pillar.name) || normalize(r.pillar_name) === normalize(pillar.key);
        });

        if (sentimentMatch && typeof sentimentMatch.sentiment_score === 'number') {
          const score = Math.max(0, Math.min(10, Number(sentimentMatch.sentiment_score)));
          nextPillars[k] = { ...nextPillars[k], sentiment_score: score };
        }
      });

      dispatch(setPillars(nextPillars));
    }
  }, [dispatch, pillarsData?.pillars, aiThemesData, aiSentimentData, teamId]);

  useInterventionsPlanSync(existingActionPlans, assessmentPeriodId, teamId);

  const { saveAllTasks, determineLeaderAndCategory } = useActionPlanOperations({
    selectedAssessmentPeriodId: assessmentPeriodId,
    organizationId,
    currentUserId,
    currentUserDebriefSchedule: null,
    categoryDelegates: [],
    existingActionPlans,
    actionPlanType: ACTION_PLAN_TYPE_INTERVENTIONS,
    teamId,
  });

  const isLoading =
    pillarsLoading ||
    pillarsFetching ||
    aiThemesLoading ||
    aiThemesFetching ||
    aiSentimentLoading ||
    aiSentimentFetching;

  const hasPillarsFetchStarted = !!assessmentPeriodId && !!shouldFetchData && !!teamId;

  const navigate = useNavigate();
  const handleOpenPillar = (key: string) => navigate(`/app/mobile/interventions/${key}`);

  if (isLoading && hasPillarsFetchStarted) {
    return <MobileSpinner />;
  }

  return (
    <MobilePageContent>
      <MobilePageSection>
        <SectionHeader>
          <Title>Team Feedback</Title>
        </SectionHeader>
        <SectionDescription>
          Select from the theme areas below to review team member feedback.
        </SectionDescription>
        <PillarGrid>
          {filteredPillars.map((pillar) => (
            <MobilePillarCard key={pillar.key} item={pillar} onOpen={handleOpenPillar} />
          ))}
        </PillarGrid>
      </MobilePageSection>

    </MobilePageContent>
  );
};

export default MobileInterventionsTab;

const PillarGrid = styled.div`
  display: grid;
  gap: 16px;
`;


